import React, { useState, useEffect } from 'react';
import { 
  Globe, 
  Eye, 
  Calendar, 
  Package, 
  ExternalLink, 
  Copy, 
  Trash2, 
  Edit3, 
  RefreshCw,
  Users,
  Lock,
  Unlock,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { 
  getMyPublicCatalogues, 
  deletePublicCatalogue, 
  updatePublicCatalogue,
  getPublicCatalogueStats,
  API_BASE_URL 
} from '../api';

const MyCatalogueView = () => {
  const [catalogues, setCatalogues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCatalogues: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [selectedCatalogue, setSelectedCatalogue] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [catalogueStats, setCatalogueStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const userRole = localStorage.getItem('role')?.toLowerCase();
    setIsAdmin(userRole === 'admin');
    fetchCatalogues();
  }, []);

  const showNotification = (message, type = 'info', duration = 3000) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, duration);
  };

  const fetchCatalogues = async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await getMyPublicCatalogues(token, page, 10);
      
      if (response.data.success) {
        setCatalogues(response.data.data.catalogues);
        setPagination(response.data.data.pagination);
      } else {
        throw new Error(response.data.message || 'Failed to fetch catalogues');
      }
    } catch (err) {
      console.error('Error fetching catalogues:', err);
      setError(err.message || 'Failed to fetch catalogues');
      showNotification('Failed to fetch catalogues', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCatalogue = async () => {
    if (!selectedCatalogue) return;

    try {
      const token = localStorage.getItem('token');
      await deletePublicCatalogue(selectedCatalogue._id, token);
      
      setCatalogues(prev => prev.filter(cat => cat._id !== selectedCatalogue._id));
      setPagination(prev => ({
        ...prev,
        totalCatalogues: prev.totalCatalogues - 1
      }));
      
      showNotification('Catalogue deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setSelectedCatalogue(null);
    } catch (error) {
      console.error('Error deleting catalogue:', error);
      showNotification('Failed to delete catalogue', 'error');
    }
  };

  const handleToggleActive = async (catalogue) => {
    try {
      const token = localStorage.getItem('token');
      await updatePublicCatalogue(catalogue._id, { isActive: !catalogue.isActive }, token);
      
      setCatalogues(prev => prev.map(cat => 
        cat._id === catalogue._id 
          ? { ...cat, isActive: !cat.isActive }
          : cat
      ));
      
      showNotification(
        `Catalogue ${!catalogue.isActive ? 'activated' : 'deactivated'} successfully`, 
        'success'
      );
    } catch (error) {
      console.error('Error updating catalogue:', error);
      showNotification('Failed to update catalogue', 'error');
    }
  };

  const handleCopyLink = (catalogue) => {
    const publicUrl = `${window.location.origin}/public-catalogue/${catalogue.publicId}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      showNotification('Public link copied to clipboard!', 'success');
    }).catch(() => {
      showNotification('Failed to copy link', 'error');
    });
  };

  const handleViewStats = async (catalogue) => {
    try {
      const token = localStorage.getItem('token');
      const response = await getPublicCatalogueStats(catalogue._id, token);
      
      if (response.data.success) {
        setCatalogueStats(response.data.data);
        setSelectedCatalogue(catalogue);
        setIsStatsModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching catalogue stats:', error);
      showNotification('Failed to fetch catalogue statistics', 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (catalogue) => {
    if (!catalogue.isActive) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircle size={12} className="mr-1" />
          Inactive
        </span>
      );
    }
    
    if (catalogue.expiresAt && new Date() > new Date(catalogue.expiresAt)) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle size={12} className="mr-1" />
          Expired
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle size={12} className="mr-1" />
        Active
      </span>
    );
  };

  const renderCatalogueCard = (catalogue) => (
    <div key={catalogue._id} className="bg-white rounded-xl shadow-lg border border-purple-100 p-6 hover:shadow-xl transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-purple-700 mb-2">{catalogue.title}</h3>
          {catalogue.description && (
            <p className="text-gray-600 text-sm mb-3">{catalogue.description}</p>
          )}
          {isAdmin && catalogue.user && (
            <div className="flex items-center text-sm text-gray-500 mb-2">
              <Users size={14} className="mr-1" />
              <span>Created by: {catalogue.user.name} ({catalogue.user.email})</span>
            </div>
          )}
        </div>
        {getStatusBadge(catalogue)}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center text-gray-600">
          <Package size={16} className="mr-2" />
          <span>{catalogue.kitIds?.length || 0} kit{catalogue.kitIds?.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Eye size={16} className="mr-2" />
          <span>{catalogue.stats?.viewCount || 0} views</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Calendar size={16} className="mr-2" />
          <span>Created {formatDate(catalogue.createdAt)}</span>
        </div>
        <div className="flex items-center text-gray-600">
          {catalogue.password ? (
            <>
              <Lock size={16} className="mr-2" />
              <span>Password Protected</span>
            </>
          ) : (
            <>
              <Unlock size={16} className="mr-2" />
              <span>Public Access</span>
            </>
          )}
        </div>
      </div>

      {catalogue.expiresAt && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center text-yellow-700 text-sm">
            <AlertCircle size={16} className="mr-2" />
            <span>Expires on {formatDate(catalogue.expiresAt)}</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => handleCopyLink(catalogue)}
          className="flex items-center px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
        >
          <Copy size={14} className="mr-1" />
          Copy Link
        </button>
        
        <a
          href={`/public-catalogue/${catalogue.publicId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
        >
          <ExternalLink size={14} className="mr-1" />
          View Public
        </a>
        
        <button
          onClick={() => handleViewStats(catalogue)}
          className="flex items-center px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
        >
          <Eye size={14} className="mr-1" />
          Stats
        </button>
        
        <button
          onClick={() => handleToggleActive(catalogue)}
          className={`flex items-center px-3 py-2 rounded-lg transition-colors text-sm ${
            catalogue.isActive 
              ? 'bg-orange-500 text-white hover:bg-orange-600' 
              : 'bg-green-500 text-white hover:bg-green-600'
          }`}
        >
          {catalogue.isActive ? (
            <>
              <XCircle size={14} className="mr-1" />
              Deactivate
            </>
          ) : (
            <>
              <CheckCircle size={14} className="mr-1" />
              Activate
            </>
          )}
        </button>
        
        <button
          onClick={() => {
            setSelectedCatalogue(catalogue);
            setIsDeleteModalOpen(true);
          }}
          className="flex items-center px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
        >
          <Trash2 size={14} className="mr-1" />
          Delete
        </button>
      </div>
    </div>
  );

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div className="flex justify-center items-center space-x-2 mt-6">
        <button
          onClick={() => fetchCatalogues(pagination.currentPage - 1)}
          disabled={!pagination.hasPrevPage}
          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        
        <span className="px-4 py-2 text-gray-600">
          Page {pagination.currentPage} of {pagination.totalPages}
        </span>
        
        <button
          onClick={() => fetchCatalogues(pagination.currentPage + 1)}
          disabled={!pagination.hasNextPage}
          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-purple-700 mb-2">
              {isAdmin ? 'All Catalogues' : 'My Catalogues'}
            </h1>
            <p className="text-gray-600">
              {isAdmin 
                ? 'Manage all public catalogues created by users' 
                : 'View and manage your public catalogues'
              }
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={() => fetchCatalogues(pagination.currentPage)}
              disabled={isLoading}
              className="flex items-center px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 transition-colors"
            >
              <RefreshCw size={16} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Catalogues</p>
                <p className="text-2xl font-bold text-purple-700">{pagination.totalCatalogues}</p>
              </div>
              <Globe size={24} className="text-purple-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Catalogues</p>
                <p className="text-2xl font-bold text-green-700">
                  {catalogues.filter(cat => cat.isActive).length}
                </p>
              </div>
              <CheckCircle size={24} className="text-green-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Views</p>
                <p className="text-2xl font-bold text-blue-700">
                  {catalogues.reduce((total, cat) => total + (cat.stats?.viewCount || 0), 0)}
                </p>
              </div>
              <Eye size={24} className="text-blue-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Kits</p>
                <p className="text-2xl font-bold text-orange-700">
                  {catalogues.reduce((total, cat) => total + (cat.kitIds?.length || 0), 0)}
                </p>
              </div>
              <Package size={24} className="text-orange-400" />
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <XCircle size={48} className="mx-auto text-red-400 mb-4" />
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => fetchCatalogues()}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : catalogues.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-12 text-center">
            <Globe size={48} className="mx-auto text-purple-200 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No catalogues found</h3>
            <p className="text-gray-600 mb-6">
              {isAdmin 
                ? 'No catalogues have been created by users yet.' 
                : 'You haven\'t created any public catalogues yet.'
              }
            </p>
            {!isAdmin && (
              <button
                onClick={() => window.location.href = '/generate'}
                className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-semibold"
              >
                Create Your First Catalogue
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {catalogues.map(renderCatalogueCard)}
            </div>
            {renderPagination()}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedCatalogue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center mb-4">
              <Trash2 size={24} className="text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Delete Catalogue</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedCatalogue.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedCatalogue(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCatalogue}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {isStatsModalOpen && selectedCatalogue && catalogueStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Eye size={24} className="text-purple-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Statistics for "{selectedCatalogue.title}"
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsStatsModalOpen(false);
                  setSelectedCatalogue(null);
                  setCatalogueStats(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle size={24} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-700">Total Views</p>
                    <p className="text-2xl font-bold text-purple-900">{catalogueStats.stats.viewCount}</p>
                  </div>
                  <Eye size={24} className="text-purple-400" />
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-700">Unique Viewers</p>
                    <p className="text-2xl font-bold text-blue-900">{catalogueStats.stats.uniqueViewerCount ?? catalogueStats.stats.uniqueViewers?.length ?? 0}</p>
                  </div>
                  <Users size={24} className="text-blue-400" />
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-700">Last Viewed</p>
                    <p className="text-sm font-bold text-green-900">
                      {catalogueStats.stats.lastViewed 
                        ? formatDate(catalogueStats.stats.lastViewed)
                        : 'Never'
                      }
                    </p>
                  </div>
                  <Calendar size={24} className="text-green-400" />
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Created:</strong> {formatDate(catalogueStats.createdAt)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-5 right-5 p-4 rounded-lg shadow-lg text-white z-[100] flex items-center ${
          notification.type === 'success' ? 'bg-green-500' : 
          notification.type === 'error' ? 'bg-red-500' : 
          'bg-blue-500'
        }`}>
          {notification.message}
          <button onClick={() => setNotification(null)} className="ml-4 text-white hover:text-gray-200">
            <XCircle size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default MyCatalogueView;
